<script lang="ts">
	import ErrorNotice from '../common/ErrorNotice.svelte'
	import LabelInput from '../common/LabelInput.svelte'
	import { checkOrderStatus, encodeSecretOrderKey } from '$lib/js/helpers'
	import { getInvoiceId, getOrder } from '$lib/js/magento/account/account'
	import { trackOrderSchema } from '$lib/js/schema'
	import { storeFindOrderModal, storeIssueOrder, storeOrderIssuesModal } from '$lib/js/stores'
	import { PUBLIC_SITE_URL } from '$env/static/public'
	import { goto } from '$app/navigation'
	import { t } from 'svelte-i18n-lingui'
	import type { Issue } from '$lib/js/types'
	import { getContextScope } from '$lib/js/helpers/i18n'
	import { fetchJSON } from '$lib/js/helpers/http'

	export let type: '' | 'bg' = ''
	export let action: 'find' | 'return' | 'bill' | 'bill-logged' | 'cancel' | 'correct-data' | 'order-status'
	export let issue: Issue | null = null

	const scope = getContextScope()

	let textarea: string
	let textarea_error: boolean | undefined = undefined
	let placeholder = $t`Indicate here the data to change.`

	$: values = { increment_id: '', email: '' }
	$: errors = { increment_id: '', email: '' }

	async function handleSubmit() {
		await trackOrderSchema.validate(values, { abortEarly: false })
		await goToOrder()
	}

	async function goToOrder() {
		const order = await getOrder({ incrementId: values.increment_id, email: values.email, scope: scope })

		if (order) {
			if (action === 'find' || action === 'return') {
				$storeIssueOrder = order
				$storeFindOrderModal = false

				if (action === 'return') {
					issue = null
				}

				$storeOrderIssuesModal = action
			} else if (action === 'cancel' || action === 'bill' || action === 'correct-data') {
				if (action === 'correct-data' && !textarea) {
					textarea_error = true
				} else {
					let msg_subject = ''

					switch (action) {
						case 'cancel':
							msg_subject = $t`Cancel order`
							break
						case 'bill':
							msg_subject = $t`Request bill`
							break
						case 'correct-data':
							msg_subject = $t`Incorrect or missing data`
							break
						default:
							break
					}

					await fetchJSON(`/${scope}/endpoints/create_ticket`, {
						method: 'POST',
						json: {
							action: action,
							order_num: order?.order?.increment_id,
							order_id: order?.order?.entity_id,
							email: values.email,
							products: {},
							subject: msg_subject,
							comments: action === 'correct-data' ? textarea : '',
						},
					})
					$storeOrderIssuesModal = action
				}
			} else if (action === 'bill-logged') {
				const invoiceId = checkOrderStatus(order.order.status) ? await getInvoiceId(order.order, { scope: scope }) : null

				if (invoiceId) {
					window.open(`${PUBLIC_SITE_URL}/pdf_invoice_frontend/invoice/pdfdownload/?invoice_id=${invoiceId}`, '_blank')
				}
			} else if (action === 'order-status') {
				await goto(`/${scope}/sales/order/view/order_id/${order.order.entity_id}?key=${encodeSecretOrderKey(values.email)}`)
			}
		} else {
			$storeOrderIssuesModal = 'not-found'
		}
	}
</script>


<div class="find-order {type}">
	<form class="find-order__form" on:submit|preventDefault={() => { handleSubmit() }}>
		<div class="find-order__row">
			<div class="u-input">
				<LabelInput id="increment" label={$t`Order n°`} placeholder="2000012345" bind:value={values.increment_id}
										background={type === "contact" ? "white" : "var(--grey)"} />
				<ErrorNotice error={errors.increment_id} />
			</div>

			<div class="u-input">
				<LabelInput id="email" label={$t`Email`} placeholder={$t`cliente@gmail.com`} bind:value={values.email}
										background={type === "contact" ? "white" : "var(--grey)"} />
				<ErrorNotice error={errors.email} />
			</div>

			{#if action === "correct-data"}
				<div class="textarea">
					<label for="textarea">{$t`Comments`}</label>
					<textarea id="textarea" bind:value={textarea} {placeholder} />
					{#if textarea_error}
						<p class="error">{$t`Please, indicate the corresponding info`}</p>
					{/if}
				</div>
			{/if}
		</div>

		<div class="button">
			<button class="u-btn u-btn--web" type="submit">
				{#if action === "find" || action === "return" || action === "correct-data" || action === "cancel"}
					{$t`Continue`}
				{:else if action === "bill" || action === "bill-logged"}
					{$t`Request bill`}
				{:else}
					{$t`View order status`}
				{/if}
				<span class="icon icon-arr-right" />
			</button>
		</div>
	</form>
</div>


<style lang="scss">
  .error {
    margin-top: 5px;
    font-size: 12px;
    color: var(--red);
  }

  .textarea {
    label {
      display: block;
      margin-bottom: 10px;
    }
  }

  textarea {
    width: calc(100% - 24px);
    min-height: 80px;
    border: none;
    border-radius: 8px;
    background: var(--grey);
    padding: 10px 12px;
    font-family: 'Sofia Pro', sans-serif;

    &::placeholder {
      color: var(--grey--b);
    }
  }

  .find-order {
    width: 100%;

    .u-input {
      margin-bottom: 12px;
    }
  }

  .bg {
    .find-order {
      &__form {
        background-color: white;
        border-radius: 10px;
        box-shadow: 0px 4px 20px 10px rgba(0, 0, 0, 0.12);
        padding: 25px 20px;

        @media (min-width: 769px) {
          padding: 25px 30px 30px;
        }
      }
    }
  }

  .button {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;

    button {
      @media (max-width: 480px) {
        width: 100%;
      }
    }

    span.icon {
      font-size: 11px;
      display: inline-block;
      transition: all linear 0.125s;
      padding-left: 9px;
    }

    @media (min-width: 769px) {
      &:hover {
        span.icon {
          transform: translateX(8px);
        }
      }
    }
  }
</style>